import { useEffect } from 'react';
import type { ReactNode } from 'react';
import Link from '@/components/base/TransitionLink';
import Header from '@/components/feature/Header';
import Frame, { SHELL, INSET_X, LINE } from '@/components/base/Frame';
import SectionLabel from '@/components/base/SectionLabel';
import ErrorBoundary from '@/components/base/ErrorBoundary';
import Chapter from '@/pages/services/customer-operations/components/Chapter';
import { brand, navLinks } from '@/mocks/home';
import SectionLink from '@/components/base/SectionLink';
import type { financeOperations } from '@/mocks/servicePages';
import SystemSlot from './SystemSlot';
import FrictionList from './FrictionList';

export type ServicePageData = typeof financeOperations;

interface Props {
  data: ServicePageData;
  /* Section 03 component (delivered separately). Falls back to a reserved, empty slot. */
  system?: ReactNode;
}

const H2 = 'mt-4 text-3xl font-light leading-[1.1] tracking-[-0.02em] text-foreground-950 lg:text-[clamp(30px,2.9vw,42px)]';

/* Mirrors the approved Customer Operations layout 1:1, content-driven. */
export default function ServicePageTemplate({ data, system }: Props) {
  const { intro, friction, implement, consult } = data;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = data.docTitle;
  }, [data.docTitle]);

  return (
    <div className="relative min-h-screen bg-background-50">
      <Header />
      <main>
        <Frame>
          {/* 01 / Introduction */}
          <Chapter id="top" headerGap>
            <div className={`${SHELL} flex flex-col`}>
              <nav aria-label="Breadcrumb" className={`border-b ${LINE} py-4 ${INSET_X}`}>
                <ol className="flex flex-wrap items-center gap-2 font-label text-[11px] uppercase tracking-[0.12em] text-foreground-500">
                  <li><Link to="/" className="cursor-target cursor-pointer whitespace-nowrap hover:text-primary-500">Home</Link></li>
                  <li aria-hidden="true">/</li>
                  <li><SectionLink section="services" className="cursor-target cursor-pointer whitespace-nowrap hover:text-primary-500">Services</SectionLink></li>
                  <li aria-hidden="true">/</li>
                  <li className="text-foreground-900">{data.crumb}</li>
                </ol>
              </nav>
              <div className={`grid grid-cols-1 gap-12 pb-12 pt-14 md:pt-20 lg:grid-cols-[minmax(0,7fr)_minmax(0,4fr)] lg:gap-16 lg:pb-16 lg:pt-28 ${INSET_X}`}>
                <div>
                  <SectionLabel index="01" label={intro.label} />
                  <h1 className="mt-6 text-4xl font-light leading-[1.06] tracking-[-0.02em] text-foreground-950 md:text-5xl lg:text-[60px]">
                    <span className="lg:whitespace-nowrap">{intro.line1}</span>
                    <br />
                    <span className="lg:whitespace-nowrap">{intro.line2}</span>
                  </h1>
                  <p className="mt-8 max-w-[34rem] text-base font-light leading-relaxed text-foreground-700 md:text-lg">{intro.desc}</p>
                  <Link
                    to="/contact"
                    className="group cursor-target mt-10 inline-flex cursor-pointer items-center gap-3 whitespace-nowrap border-b border-primary-500 pb-1 text-sm text-primary-600 transition-colors hover:text-primary-700"
                  >
                    Book a consultation
                    <span className="transition-transform duration-300 group-hover:translate-x-1">&rarr;</span>
                  </Link>
                </div>
                <dl className={`self-end border-t ${LINE} font-label text-[11px] uppercase tracking-[0.12em]`}>
                  {intro.meta.map((m) => (
                    <div key={m.k} className={`flex justify-between gap-6 border-b ${LINE} py-3`}>
                      <dt className="text-foreground-500">{m.k}</dt>
                      <dd className="text-right text-foreground-900">{m.v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </Chapter>

          {/* 02 / Operational friction */}
          <Chapter id="friction">
            <div className={`${SHELL} flex flex-col py-10 md:py-14`}>
              <div className={INSET_X}>
                <SectionLabel index="02" label="Operational friction" />
                <h2 className={H2}>{friction.title}</h2>
              </div>
              <FrictionList items={friction.items} className="mt-10 md:mt-14" />
            </div>
          </Chapter>

          {/* 03 / Main system — component slot */}
          <Chapter id="system">
            <div className={`${SHELL} flex flex-col`}>
              <div className={`flex flex-col gap-3 bg-primary-50/70 pb-8 pt-10 md:flex-row md:items-end md:justify-between md:pb-10 md:pt-14 ${INSET_X}`}>
                <div>
                  <SectionLabel index="03" label={data.system.label} />
                  <h2 className={H2}>{data.system.title}</h2>
                </div>
                <p className="max-w-[24rem] text-sm font-light leading-relaxed text-foreground-600 md:text-right">{data.system.note}</p>
              </div>
              <div className={`border-t ${LINE}`}>
                <ErrorBoundary>{system ?? <SystemSlot name={data.system.label} />}</ErrorBoundary>
              </div>
              <p className={`border-t ${LINE} py-3 font-label text-[10px] uppercase tracking-[0.12em] text-foreground-500 ${INSET_X}`}>
                Illustrative workflow — not a client case
              </p>
            </div>
          </Chapter>

          {/* 04 / What we implement */}
          <Chapter id="implement">
            <div className={`${SHELL} flex flex-col py-10 md:py-14`}>
              <div className={INSET_X}>
                <SectionLabel index="04" label="What we implement" />
                <h2 className={`${H2} max-w-[22ch]`}>{implement.title}</h2>
              </div>
              <ol className={`mt-10 grid grid-cols-1 border-t ${LINE} md:mt-14 md:grid-cols-3`}>
                {implement.items.map((c, i) => (
                  <li key={c.index} className={`group flex flex-col border-b ${LINE} py-8 md:border-b-0 md:py-10 ${i > 0 ? `md:border-l ${LINE}` : ''} ${INSET_X}`}>
                    <div className="flex items-center gap-3">
                      <span className="font-label text-xs tracking-[0.1em] text-primary-500">{c.index}</span>
                      <span className="h-px flex-1 bg-primary-200/70 transition-colors duration-500 group-hover:bg-primary-500" />
                    </div>
                    <h3 className="mt-8 text-xl font-light tracking-[-0.01em] text-foreground-950 md:mt-14 lg:text-2xl">{c.title}</h3>
                    <p className="mt-3 max-w-[22rem] text-base font-light leading-relaxed text-foreground-700">{c.desc}</p>
                  </li>
                ))}
              </ol>
            </div>
          </Chapter>

          {/* 05 / Consultation */}
          <Chapter id="consultation">
            <div className={`${SHELL} grid grid-cols-1 lg:grid-cols-[minmax(0,7fr)_minmax(0,4fr)]`}>
              <div className={`bg-primary-50/70 py-12 md:py-20 ${INSET_X}`}>
                <SectionLabel index="05" label="Consultation" />
                <h2 className="mt-6 text-4xl font-light leading-[1.06] tracking-[-0.02em] text-foreground-950 lg:text-[52px]">{consult.title}</h2>
                <p className="mt-6 max-w-[30rem] text-base font-light leading-relaxed text-foreground-700 md:text-lg">{consult.desc}</p>
              </div>
              <div className={`flex items-end border-t ${LINE} py-8 lg:border-l lg:border-t-0 lg:py-20 ${INSET_X}`}>
                <Link
                  to="/contact"
                  className="cursor-target flex w-full cursor-pointer items-center justify-between whitespace-nowrap bg-primary-500 px-6 py-4 text-sm text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
                >
                  Discuss a Workflow
                  <span>&rarr;</span>
                </Link>
              </div>
            </div>
          </Chapter>

          <footer className="pb-10 md:pb-14">
            <div className={SHELL}>
              <div className={`flex flex-col gap-6 border-y ${LINE} py-8 ${INSET_X} md:flex-row md:items-end md:justify-between`}>
                <div>
                  <Link to="/" className="cursor-target cursor-pointer text-lg text-foreground-950">
                    {brand.name}<span className="ml-1 font-light text-foreground-600">/</span>
                  </Link>
                  <p className="mt-2 text-sm text-foreground-700">{brand.descriptor}</p>
                  <p className="mt-4 font-label text-[11px] text-foreground-500">&copy; 2026 {brand.name}. All rights reserved.</p>
                </div>
                <nav className="flex flex-wrap gap-x-8 gap-y-2">
                  {navLinks.map((l) => (
                    <SectionLink key={l.href} section={l.href} className="cursor-target cursor-pointer whitespace-nowrap text-sm text-foreground-700 hover:text-primary-500">{l.label}</SectionLink>
                  ))}
                  {brand.linkedin && <a href={brand.linkedin} target="_blank" rel="noopener noreferrer" className="cursor-target cursor-pointer whitespace-nowrap text-sm text-foreground-700 hover:text-primary-500">LinkedIn</a>}
                </nav>
              </div>
            </div>
          </footer>
        </Frame>
      </main>
    </div>
  );
}